/* eslint-disable @khanacademy/ts-no-error-suppressions */
/* eslint-disable no-invalid-this */
import "../../stylesheets/search-package/search.less";

import {t} from "@lingui/core/macro";
import * as React from "react";
import {findDOMNode} from "react-dom";
import _ from "underscore";

import Link from "@khanacademy/wonder-blocks-link";
import {CircularSpinner} from "@khanacademy/wonder-blocks-progress-spinner";
import {markMinorConversions} from "@khan/analytics-learning-time";
import {PageloadMarker} from "@khan/analytics-performance";
import {Errors, KAError} from "@khan/logging";

import $ from "#jquery/jquery.ts";
// eslint-disable-next-line no-restricted-imports -- TODO: Use React Portals instead.
import deprecatedReactRender from "#rendering/deprecated-react-render.tsx";

import SearchEngines from "./cse-search-engines.ts";

// 'google' is an untyped global
declare let google: any;

const MutationObserver: any =
    window.MutationObserver ||
    // @ts-expect-error - TS2339 - Property 'WebKitMutationObserver' does not exist on type 'Window & typeof globalThis'.
    window.WebKitMutationObserver ||
    // @ts-expect-error - TS2339 - Property 'MozMutationObserver' does not exist on type 'Window & typeof globalThis'.
    window.MozMutationObserver;

const loadGoogleCustomSearch = _.memoize(function (kaLocale) {
    const deferred = $.Deferred();
    // @ts-expect-error - TS2339 - Property '__gcse' does not exist on type 'Window & typeof globalThis'.
    window.__gcse = {
        parseTags: "explicit",
        callback: deferred.resolve,
    };

    const cx = SearchEngines[kaLocale] || SearchEngines["en"];
    const gcse = document.createElement("script");
    gcse.type = "text/javascript";
    gcse.async = true;
    gcse.src = "https://cse.google.com/cse/cse.js?cx=" + encodeURIComponent(cx);
    const s = document.getElementsByTagName("script")[0];
    s.parentNode?.insertBefore(gcse, s);

    return deferred.promise();
});

type ConversionExtras = Record<string, any>;

type Props = {
    acceptSpellingCorrection?: (query: string) => unknown;
    /**
     * Extras to-be added to any BigBingo conversions.
     */
    conversionExtras?: ConversionExtras;
    isPopup: boolean;
    kaLocale: string;
    numResultsToShow: number;
    openInNewWindow: boolean;
    query?: string;
    showSearchBar?: boolean;
    // A callback method that sends the results we receive from Google Custom
    // Search up to an ancestor component.
    // It's optional because it's only used in the search header dropdown, not
    // on the /search page.
    // Accepts (name, results) as parameters and returns a list of DOM elements.
    onSearchResultsAdded?: (results: Array<HTMLAnchorElement>) => unknown;
    onFooterResultsAdded?: (results: Array<boolean>) => unknown;
    focusedIndex: number;
    onKeyDown?: (e: React.KeyboardEvent) => unknown;
};

type DefaultProps = {
    isPopup: Props["isPopup"];
    kaLocale: Props["kaLocale"];
    numResultsToShow: Props["numResultsToShow"];
    openInNewWindow: Props["openInNewWindow"];
    showSearchBar: Props["showSearchBar"];
};

type State = {
    loaded: boolean;
    searchResultsTitleLinks: Array<HTMLAnchorElement>;
};

class GoogleCustomSearchResults extends React.Component<Props, State> {
    static defaultProps: DefaultProps = {
        isPopup: false,
        numResultsToShow: 10,
        openInNewWindow: true,
        showSearchBar: false,
        kaLocale: "en",
    };

    state: State = {
        loaded: false,
        searchResultsTitleLinks: [],
    };

    componentDidMount() {
        this._isMounted = true;

        loadGoogleCustomSearch(this.props.kaLocale).then(() => {
            if (!this._isMounted) {
                return;
            }

            // Sometimes we pass in an explicit `null` as kaLocale, so
            // we have to do this default check twice.
            // TODO(emily): // Don't pass in `null` here.
            const domain =
                !this.props.kaLocale || this.props.kaLocale === "en"
                    ? "www"
                    : this.props.kaLocale;
            const sitesearch = domain + ".khanacademy.org";

            const resultsData = {
                // eslint-disable-next-line react/no-string-refs
                div: findDOMNode(this.refs.results),
                tag: this.props.showSearchBar
                    ? "searchresults"
                    : "searchresults-only",
                attributes: {
                    // We take advantage of the fact that our translated
                    // sites are always <ka-lang>.khanacademy.org.  Note
                    // we always show khanacademy.org results even on dev.
                    as_sitesearch: sitesearch,
                    enableImageSearch: false,
                    linkTarget: this.props.openInNewWindow
                        ? "_blank"
                        : "_parent",
                    resultSetSize: undefined,
                    // TODO(emily): Figure out how to get google analytics to
                    // work?
                },
            } as const;

            // When any number of results more than 8 are rendered, the google
            // custom results only shows 4. If the attribute is left off, the
            // custom results shows 10. So, we only add the attribute if the
            // number is <= 8, and throw an error if we're not otherwise trying
            // to render 10.
            if (
                this.props.numResultsToShow > 0 &&
                this.props.numResultsToShow <= 8
            ) {
                // @ts-expect-error - TS2540 - Cannot assign to 'resultSetSize' because it is a read-only property.
                resultsData.attributes.resultSetSize =
                    this.props.numResultsToShow;
            } else if (this.props.numResultsToShow !== 10) {
                throw new KAError(
                    "Can't render " +
                        this.props.numResultsToShow +
                        " results, only [1..8, 10]",
                    Errors.Internal,
                );
            }

            if (this.props.showSearchBar) {
                const searchBarData = {
                    // eslint-disable-next-line react/no-string-refs
                    div: findDOMNode(this.refs.searchbar),
                    tag: "searchbox",
                } as const;

                this.cseElement = google.search.cse.element.legacyReactRender(
                    searchBarData,
                    resultsData,
                );
            } else {
                this.cseElement = google.search.cse.element.render(resultsData);
            }

            this.executeQuery(this.props.query);

            // call logic to assign relevant click handers for conversions
            searchConversions(this);
            addSuggestedSpellingClickHandler(
                this,
                this.props.acceptSpellingCorrection,
            );

            // We need to wait a second for the elements to get accessible in the DOM
            // before we can grab and update the Google logo.
            // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
            // eslint-disable-next-line no-restricted-syntax
            const timer = setTimeout(() => {
                const googleBrandingImageList = document.getElementsByClassName(
                    "gcsc-branding-img-noclear",
                );
                const googleBrandingImage = googleBrandingImageList.length
                    ? googleBrandingImageList[0]
                    : null;
                if (googleBrandingImage) {
                    googleBrandingImage.setAttribute("alt", "Google");
                }
            }, 1000);
            this.timers.push(timer);
        });
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (this.cseElement) {
            if (this.props.query) {
                // Check whether the query has actually changed before calling
                // `.execute` again. Triggering this again doesn't actually
                // cause a re-render of the search results, but it does seem to
                // invalidate any of the link presses that are currently
                // occuring, so if a link is clicked and this component is then
                // re-rendered (i.e. when you click on one of the links in the
                // search dropdown), the link won't actually be followed.
                if (this.props.query !== prevProps.query) {
                    this.executeQuery(this.props.query);
                }
            } else {
                this.cseElement.clearAllResults();
            }
        }

        const {focusedIndex} = this.props;
        if (
            focusedIndex !== -1 &&
            focusedIndex !== prevProps.focusedIndex &&
            this.state.searchResultsTitleLinks[focusedIndex]
        ) {
            this.state.searchResultsTitleLinks[focusedIndex].focus();
        }
    }

    componentWillUnmount() {
        this._isMounted = false;
        this.timers.forEach(clearTimeout);
    }

    // TODO(briangenisio): This is a hack to remove the deprecated call to
    // this.isMounted() but is still considered an anti-pattern.  Replace
    // with code which does not rely on the mounted state like Redux or GraphQL
    _isMounted = false;

    timers: Array<any | number> = [];

    _mutationObserver: any | null | undefined = null;
    _sortByObserver: any | null | undefined = null;

    _originalRelevanceLabel: string | null | undefined = null;
    _originalDateLabel: string | null | undefined = null;

    cseElement: any | null | undefined = null;

    shouldShowSpinner(): boolean {
        if (!MutationObserver) {
            return false;
        }

        return !this.state.loaded;
    }

    translateTabHeaders() {
        // Translate the "All" tab header.
        Array.from(document.querySelectorAll(".gsc-tabHeader"))
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            .filter((tabHeader) => tabHeader && tabHeader.innerText === "All")
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            .forEach((tabHeader) => (tabHeader.innerText = t`All`));
    }

    translateSortByDropdown() {
        // Translate the "Sort by:" dropdown label.
        const sortByLabel = document.querySelector(".gsc-orderby-label");
        if (sortByLabel) {
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            sortByLabel.innerText = t`Sort by:`;
        }

        // Translate the dropdown options labels.
        const sortByOptions = Array.from(
            document.querySelectorAll(".gsc-option-menu-item > .gsc-option"),
        );
        // HACK(michaelpolyak): For some languages Google CSE is incorrectly
        // translating the options. We're going to inject our own translations,
        // and are making the assumption that the dropdown will always have
        // exactly two options in this exact order: "Relevance", "Date".
        if (sortByOptions.length !== 2) {
            return;
        }

        const translatedRelevanceLabel = t`Relevance`;
        const translatedDateLabel = t`Date`;

        // No translations, no need to do anything.
        if (
            translatedRelevanceLabel === "Relevance" &&
            translatedDateLabel === "Date"
        ) {
            return;
        }

        // Record the original options strings, as we'll need to compare against
        // them to determine what to translate the selected option label to.
        if (!this._originalRelevanceLabel) {
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            this._originalRelevanceLabel = sortByOptions[0].innerText;
        }
        if (!this._originalDateLabel) {
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            this._originalDateLabel = sortByOptions[1].innerText;
        }

        // Now translate the options labels.
        // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
        sortByOptions[0].innerText = translatedRelevanceLabel;
        // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
        sortByOptions[1].innerText = translatedDateLabel;

        // This is a separate function as it may also be called from a mutation
        // observer.
        const translateSelectedOption = () => {
            const selectedOption = document.querySelector(
                ".gsc-selected-option",
            );
            if (!selectedOption) {
                return;
            }

            // Maybe translate the currently selected option, by checking
            // against the original options strings.
            // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            if (selectedOption.innerText === this._originalRelevanceLabel) {
                // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
                selectedOption.innerText = translatedRelevanceLabel;
                // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
            } else if (selectedOption.innerText === this._originalDateLabel) {
                // @ts-expect-error - TS2339 - Property 'innerText' does not exist on type 'Element'.
                selectedOption.innerText = translatedDateLabel;
            }

            const optionContainer = document.querySelector(
                ".gsc-selected-option-container",
            );
            // This keeps the the dropdown caret from dropping below the
            // dropdown itself when the selected option text is changed.
            if (optionContainer) {
                // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'Element'.
                optionContainer.style.setProperty("display", "flex");
                // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'Element'.
                optionContainer.style.setProperty(
                    "width",
                    "fit-content",
                    "important",
                );
            }

            const optionMenu = document.querySelector(".gsc-option-menu");
            // This makes the dropdown menu contain the options labels.
            if (optionMenu) {
                // @ts-expect-error - TS2339 - Property 'style' does not exist on type 'Element'.
                optionMenu.style.setProperty(
                    "width",
                    "fit-content",
                    "important",
                );
            }

            const sortByContainer = document.querySelector(
                ".gsc-orderby-container",
            );
            // Setup a mutation observer for the Sort By container. The selected
            // option label will change when user picks a different sort order,
            // requiring re-translation.
            if (sortByContainer && !this._sortByObserver) {
                this._sortByObserver = new MutationObserver(
                    (mutations: unknown) => {
                        translateSelectedOption();
                    },
                );

                this._sortByObserver.observe(sortByContainer, {
                    attributes: true,
                    subtree: true,
                });
            }
        };

        // Initially translate the selected option.
        translateSelectedOption();
    }

    improveReceivedDOMElements() {
        // HACK(diedra): This method is called when the MutationObserver tells us
        // the results have been added to the page, but we still need to wait a
        // sec before we can find and operate on those results.
        // TODO(jeff, LP-6954): Use Wonder Blocks Timing API
        // eslint-disable-next-line no-restricted-syntax
        const timer = setTimeout(() => {
            // Here we separate out the navigable links of the results that Google
            // Custom Search injected into the DOM and improve them to make them
            // more accessible to our users.
            const allGsTitleElements =
                document.getElementsByClassName("gs-title");
            const allGsTitleElementsArray =
                Array.prototype.slice.call(allGsTitleElements);
            // HACK(diedra): There are two links for every result in the list,
            // one of which is visually hidden. These two links are identical except
            // that the parent element has a class called "gsc-table-cell-thumbnail".
            const allGsTitleLinks: Array<HTMLAnchorElement> =
                allGsTitleElementsArray.filter((element) => {
                    return (
                        element.tagName === "A" &&
                        element.href &&
                        !element.parentElement.classList.contains(
                            "gsc-table-cell-thumbnail",
                        )
                    );
                });

            const {onKeyDown, onSearchResultsAdded} = this.props;
            if (onKeyDown && onSearchResultsAdded) {
                const results: Array<HTMLAnchorElement> = [];
                for (const oldLink of allGsTitleLinks) {
                    const container = document.createElement("div");

                    // TODO: This should be using React Portals.
                    deprecatedReactRender(
                        <Link
                            href={oldLink.href}
                            safeWithNav={() =>
                                markMinorConversions([{id: "autocomplete"}])
                            }
                            className="gs-title"
                            onKeyDown={onKeyDown}
                            // @ts-expect-error - TS2769 - No overload matches this call.
                            target={oldLink.target}
                            dir={oldLink.dir}
                            data-cturl={oldLink.dataset.cturl}
                            data-ctorig={oldLink.dataset.ctorig}
                        >
                            {oldLink.text}
                        </Link>,
                        container,
                    );

                    const newLink = container.firstElementChild;
                    /**
                     * TODO(somewhatabstract, JIRA-XXXX):
                     * This should be:
                     * `if (newLink instanceof HTMLAnchorElement)`
                     */
                    if (newLink) {
                        // @ts-expect-error - TS2345 - Argument of type 'Element' is not assignable to parameter of type 'HTMLAnchorElement'.
                        results.push(newLink);
                        oldLink.replaceWith(newLink);
                    }
                }

                this.setState({searchResultsTitleLinks: results});
                onSearchResultsAdded(results);

                // Now that the results are ready, the footer can also be navigated
                // to.
                if (this.props.onFooterResultsAdded) {
                    this.props.onFooterResultsAdded(
                        results.length ? [true] : [],
                    );
                }
            }

            // Each result has a thumbnail with it which is also a link.
            // Users tabbing through the page are taken to each thumbnail even
            // though it's the same link as the title and it has no useful information
            // for users reading the page via a screen reader.
            const allGsImageElements =
                document.getElementsByClassName("gs-image");
            const allGsImageElementsArray =
                Array.prototype.slice.call(allGsImageElements);
            allGsImageElementsArray.forEach((element) => {
                if (element.tagName === "A") {
                    element.tabIndex = -1;
                    element.setAttribute("aria-hidden", true);
                }
            });

            // Inject translations for result page elements.
            this.translateTabHeaders();
            this.translateSortByDropdown();
        }, 1000);
        this.timers.push(timer);
    }

    getMutationObserver(): any | null | undefined {
        // The Google search API does not support callbacks when the content
        // is loaded. Instead, we need to watch the container that the API
        // writes into for changes. We achieve this with a MutationObserver.

        if (!MutationObserver) {
            return;
        }

        if (!this._mutationObserver) {
            const mutationObserver = new MutationObserver(
                (mutations: unknown) => {
                    mutationObserver.disconnect();

                    if (this._isMounted) {
                        this.setState({loaded: true});
                    }

                    this.improveReceivedDOMElements();
                },
            );
            this._mutationObserver = mutationObserver;
        }

        return this._mutationObserver;
    }

    executeQuery(query?: string) {
        if (query) {
            const mutationObserver = this.getMutationObserver();
            if (mutationObserver) {
                this.setState({loaded: false});

                mutationObserver.disconnect();
                // eslint-disable-next-line react/no-string-refs
                mutationObserver.observe(this.refs.results, {
                    attributes: true,
                    subtree: true,
                });
            }

            // This tells the element to search for the initial query.
            if (this.cseElement) {
                this.cseElement.execute(query);
            }
        }
    }

    render(): React.ReactNode {
        return (
            <div className="google-custom-search-results">
                {this.shouldShowSpinner() && <CircularSpinner />}
                {/* eslint-disable-next-line react/no-string-refs */}
                {this.props.showSearchBar && <div ref="searchbar" />}
                {/* eslint-disable-next-line react/no-string-refs */}
                <div ref="results" />
                <PageloadMarker
                    pageName="search_results"
                    fullyInteractive={this.state.loaded}
                    sufficientlyUsable={this.state.loaded}
                />
            </div>
        );
    }
}

const addSuggestedSpellingClickHandler = function (
    resultsContext: GoogleCustomSearchResults,
    handler?: (query: string) => unknown,
) {
    $(findDOMNode(resultsContext as any) as any).on(
        "click",
        ".gs-spelling > a",
        function (e) {
            // @ts-expect-error Property 'innerText' does not exist on type 'Element'.ts(2339)
            const linkText = e.target.innerText;
            const newQuery = linkText.replace(" site:www.khanacademy.org", "");
            if (handler) {
                handler(newQuery);
            }
        },
    );
};

// Logic for firing conversions depending on where we are (google autocomplete
// results or /search results) and what result is clicked
const searchConversions = function (resultsContext: GoogleCustomSearchResults) {
    // TODO(emily/amy): The selectors below are hacky, and could break
    // if google changes their custom search HTML. This is done
    // because the google custom search API doesn't currently (Aug
    // 2015) add an easy way to monitor search clicks.
    // TODO(amy): hack the VED param in this[data-cturl] to get result
    // rank and page position info

    const {
        conversionExtras: initialConversionExtras,
        isPopup: isAutocomplete,
        query: initialQuery,
    } = resultsContext.props;

    // JS conversions for autocomplete
    if (isAutocomplete) {
        // Component mounted meaning user viewed  autocomplete results
        autocompleteSearched(initialQuery);

        // Clicked a result
        $(findDOMNode(resultsContext as any) as any).on(
            "mousedown" as any,
            "div.gsc-webResult.gsc-result",
            function (this: any, e: MouseEvent) {
                const {query, conversionExtras} = resultsContext.props;
                const fields = getResultFields(this, isAutocomplete);
                if (fields) {
                    const isFirst = $($(this).siblings()[0]).hasClass(
                        "gcsc-branding",
                    );
                    if (isFirst) {
                        clickedFirstResult(
                            this,
                            query,
                            fields["url"],
                            fields["ved"],
                            null,
                            isAutocomplete,
                            conversionExtras,
                        );
                    }
                    clickedAnyResult(
                        this,
                        query,
                        fields["url"],
                        fields["ved"],
                        null,
                        isAutocomplete,
                        conversionExtras,
                    );
                }
            },
        );
    } else {
        // JS conversions for /search
        // Mark google results viewed
        markMinorConversions([
            {
                id: "SEARCH_viewed_google_results_JS",
                extra: {
                    ...(initialConversionExtras || {}),
                    query: initialQuery,
                },
            },
        ]);

        // Add a bigquery conversion if the first link is clicked.
        $(findDOMNode(resultsContext as any) as any).on(
            "mousedown" as any,
            ".gsc-results" +
                " > .gsc-result" +
                ' a[data-ctorig^="https://www.khanacademy.org"]',
            function (this: any, e: MouseEvent) {
                // Send a request to mark that the top link was clicked.
                // Note that this assumes that the result links are opened
                // in a new window (which they are at this time), otherwise
                // the request won't go through.
                const {query, conversionExtras} = resultsContext.props;
                const fields = getResultFields(this, isAutocomplete);
                if (fields) {
                    const page = getCurrentPage(resultsContext);
                    if (page === "1") {
                        clickedFirstResult(
                            this,
                            query,
                            fields["url"],
                            fields["ved"],
                            page,
                            isAutocomplete,
                            conversionExtras,
                        );
                    }
                }
            },
        );
        $(findDOMNode(resultsContext as any) as any).on(
            "mousedown" as any,
            " .gsc-result a",
            function (this: any, e: MouseEvent) {
                const fields = getResultFields(this, isAutocomplete);
                if (fields) {
                    const page = getCurrentPage(resultsContext);
                    const {query, conversionExtras} = resultsContext.props;
                    // Mark that any result was clicked
                    clickedAnyResult(
                        this,
                        query,
                        fields["url"],
                        fields["ved"],
                        page,
                        isAutocomplete,
                        conversionExtras,
                    );
                }
            },
        );

        // Send a request to mark that one of the additional pages
        // in the page cursor was clicked
        $(findDOMNode(resultsContext as any) as any).on(
            "click" as any,
            ".gsc-cursor-box .gsc-cursor-page",
            function (this: any, e: MouseEvent) {
                // Send a request to mark that one of the additional pages
                // in the page cursor was clicked
                const {query, conversionExtras} = resultsContext.props;
                clickedNext(query, this.innerHTML, conversionExtras);
            },
        );
    }
};

const getCurrentPage = function (element: GoogleCustomSearchResults) {
    const currentPage: any = $(findDOMNode(element as any) as any)
        .find(".gsc-cursor-current-page")
        .get(0);
    if (currentPage != null) {
        return currentPage.innerHTML;
    }
};

const getResultFields = function (linkNode: any, isAutocomplete: boolean) {
    if (isAutocomplete) {
        linkNode = $(linkNode).find("a.gs-title")[0];
    }
    // If the clicked link is in a div with the class "gs-spelling" then it
    // is a spelling correction rather than a result link. We won't mark a
    // bigbingo conversion in that case.
    if (
        linkNode.parentNode &&
        Object.values(linkNode.parentNode.classList).includes("gs-spelling")
    ) {
        return null;
    }
    const ved = 0; // TODO(amy): figure out how to extract and use the VED param
    const url = linkNode.href.match("khanacademy.org([^&]+)")[1];
    return {url: url, ved: ved};
};

const autocompleteSearched = function (query?: string) {
    // TODO(jangmi, LP-7436): Use withLocation to access SSR safe location.
    // eslint-disable-next-line no-restricted-syntax
    if (window.location.href.indexOf("/search?") === -1) {
        markMinorConversions([
            {
                id: "SEARCH_autocomplete_suggestion_searched",
                extra: {
                    query: query,
                },
            },
        ]);
    }
};

// Conversions that fire when google results are clicked
// TODO(amy): Figure out how to make this more robust to immediate page
// navigation. We should be aware that some autocomplete conversions could be
// lost if they don't go through before we navigate away.

const clickedFirstResult = function (
    clickedNode: any,
    query: string | null | undefined,
    url: any,
    ved: number,
    page: any | null | undefined,
    isAutocomplete: boolean,
    conversionExtras?: ConversionExtras,
) {
    const conversionId = isAutocomplete
        ? "SEARCH_clicked_first_autocomplete_result"
        : "SEARCH_clicked_first_result";
    const extra: ConversionExtras = {
        ...(conversionExtras || {}),
        query,
        search_mode: "google",
        destination_url: url,
        ved,
    };
    if (!isAutocomplete) {
        extra["page"] = page;
    }
    const conversionData = [
        {
            id: conversionId,
            extra: extra,
        },
    ];
    // @ts-expect-error - TS2345 - Argument of type '{ id: string; extra: ConversionExtras; }[]' is not assignable to parameter of type 'readonly Readonly<{ id: MinorConversionId; extra?: Record<any, any> | undefined; event?: AnalyticsEvent | undefined; }>[]'.
    markMinorConversions(conversionData);
};

const clickedAnyResult = function (
    clickedNode: any,
    query: string | null | undefined,
    url: any,
    ved: number,
    page: any | null | undefined,
    isAutocomplete: boolean,
    conversionExtras?: ConversionExtras,
) {
    const extra: ConversionExtras = {
        ...(conversionExtras || {}),
        query,
        search_mode: "google",
        destination_url: url,
        ved,
    };
    if (!isAutocomplete) {
        extra["page"] = page;
    }
    const conversionData = [
        {
            id: "SEARCH_took_learning_action",
            extra: extra,
        },
    ];
    if (isAutocomplete) {
        conversionData.push({
            id: "SEARCH_clicked_autocomplete_result",
            extra: extra,
        });
    } else if (page === "1") {
        // only care about page if not autocomplete
        conversionData.push({
            id: "SEARCH_clicked_first_page_result",
            extra: extra,
        });
    }
    // @ts-expect-error - TS2345 - Argument of type '{ id: string; extra: ConversionExtras; }[]' is not assignable to parameter of type 'readonly Readonly<{ id: MinorConversionId; extra?: Record<any, any> | undefined; event?: AnalyticsEvent | undefined; }>[]'.
    markMinorConversions(conversionData);
};

const clickedNext = function (
    query: string | null | undefined,
    page: any,
    conversionExtras?: ConversionExtras,
) {
    markMinorConversions([
        {
            id: "SEARCH_clicked_next",
            extra: {
                ...(conversionExtras || {}),
                query: query,
                search_mode: "google",
                page: page,
            },
        },
    ]);
};

export default GoogleCustomSearchResults;
